import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch, ApiRequestError } from '@/lib/api/client';
import { runtimeConfig } from '@/lib/config';
import type { DeviceChannelKind, Session } from '@/lib/types';

/**
 * 세션 조회·로그인·로그아웃.
 *
 * v1 은 `lib/adminTestClientSession.ts` 에서 모듈 전역 변수 + 구독자 Set + localStorage 캐시로
 * 손수 만든 스토어를 돌렸다. v2 는 같은 일을 TanStack Query 가 한다 — 중복 요청 합치기,
 * 캐시 무효화, 구독 모두 쿼리 캐시의 기본 동작이다.
 *
 * 세션을 localStorage 에 캐시하지 않는 것은 의도된 차이다. 그 캐시는 로그아웃·권한 회수 후에도
 * 최대 60초 동안 예전 권한으로 화면을 그리게 만든다. 어차피 인가는 서버가 하므로 화면이 앞서
 * 판단할 이유가 없다.
 */

const SIGNED_OUT: Session = { authenticated: false, user: null, visibleMenuKeys: [], menuPermissions: [] };

/**
 * 개발 중 백엔드가 없을 때 쓰는 목 세션. super_admin 이라 프론트가 전 메뉴를 연다
 * (`isAdministrator` → `useMenuTree`·`RequireAuth`). 백엔드 임시 인증이 주는 세션과 같은 성격이다.
 */
const MOCK_SESSION: Session = {
  authenticated: true,
  user: {
    id: 'dev-mock',
    email: 'dev@bomienc.co.kr',
    name: '개발(목 세션)',
    rank: null,
    userType: 'super_admin',
    mustChangePassword: false,
  },
  // super_admin 이라 관리자 취급 — 목록이 비어도 프론트가 전부 연다(실서버 관리자 세션과 같은 모양).
  visibleMenuKeys: [],
  menuPermissions: [],
};

export const sessionQueryKey = ['session'] as const;

async function fetchSession(signal?: AbortSignal): Promise<Session> {
  try {
    return await apiFetch<Session>('/v1/auth/session', { signal });
  } catch (error) {
    // 401 은 "아직 로그인 안 함"이라는 정상 응답이다. 오류로 올리면 화면이 에러 상태로 빠진다.
    if (error instanceof ApiRequestError && error.status === 401) return SIGNED_OUT;

    // 개발 중 백엔드에 닿지 못하면(서버 꺼짐) 목 세션으로 화면을 연다.
    //
    // "닿지 못함" 은 두 모습으로 온다:
    //  - 직접 호출(cross-origin): fetch 가 TypeError 로 거부한다.
    //  - Vite 프록시(개발 기본): 대상이 죽으면 프록시가 <b>봉투 없는</b> 5xx 를 돌려준다.
    // 살아 있는 백엔드는 5xx 라도 항상 우리 JSON 봉투({ok:false, error})를 실어 보내므로 apiError 가 있다 —
    // 그건 진짜 장애이니 목으로 덮지 않고 재시도 화면으로 드러낸다. 요청 취소(AbortError)도 여기 걸리지 않는다.
    const unreachable =
      error instanceof TypeError ||
      (error instanceof ApiRequestError && error.apiError === null);
    if (runtimeConfig().mockSessionWhenOffline && unreachable) {
      console.warn('[dev] 백엔드에 닿지 못해 목 세션으로 화면을 엽니다. 서버를 켜면 실제 세션이 쓰입니다.');
      return MOCK_SESSION;
    }

    throw error;
  }
}

export function useSession() {
  return useQuery({
    queryKey: sessionQueryKey,
    queryFn: ({ signal }) => fetchSession(signal),
    staleTime: 60_000,
    retry: false,
  });
}

export type LoginInput = { email: string; password: string };

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: LoginInput) => apiFetch<Session>('/v1/auth/login', { method: 'POST', body: input }),
    onSuccess: (session) => {
      // 본인확인이 남은 답(deviceChallenge)은 세션이 아니다 — 로그인 화면이 창을 띄우고, 맞히면 그 답으로 세션을 넣는다.
      if (session.deviceChallenge) return;
      queryClient.setQueryData(sessionQueryKey, session);
    },
  });
}

/** 새 브라우저 본인확인(라운지 DOC-138) — 고른 길(휴대폰 · 메일)로 6자리를 받는다. */
export type DeviceCodeSent = {
  verificationId: string;
  channel: DeviceChannelKind;
  maskedTarget: string;
  expiresInSeconds: number;
};

export function useSendLoginDeviceCode() {
  return useMutation({
    mutationFn: (input: { pendingId: string; channel: DeviceChannelKind }) =>
      apiFetch<DeviceCodeSent>('/v1/auth/login/device-challenge', { method: 'POST', body: input }),
  });
}

/** 받은 6자리를 맞히면 세션이 열린다. 성공 답은 로그인과 같은 모양이다. */
export function useVerifyLoginDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { pendingId: string; verificationId: string; code: string }) =>
      apiFetch<Session>('/v1/auth/login/verify-device', { method: 'POST', body: input }),
    onSuccess: (session) => {
      queryClient.setQueryData(sessionQueryKey, session);
    },
  });
}

/** 로그인 OTP(라운지 V618) — 비밀번호 다음 구글 OTP 6자리. */
export function useVerifyLoginTotp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { pendingId: string; code: string }) =>
      apiFetch<Session>('/v1/auth/login/verify-totp', { method: 'POST', body: input }),
    onSuccess: (session) => {
      queryClient.setQueryData(sessionQueryKey, session);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiFetch<void>('/v1/auth/logout', { method: 'POST' }),
    // 실패하더라도 화면의 세션은 비운다. 서버 쿠키가 남아 있다면 다음 요청에서 다시 드러난다.
    onSettled: () => {
      queryClient.setQueryData(sessionQueryKey, SIGNED_OUT);
      // 이전 사용자의 데이터가 다음 사용자 화면에 남지 않도록 캐시를 통째로 버린다.
      queryClient.clear();
    },
  });
}

/*
  비밀번호 찾기 — 휴대폰 인증 세 걸음(V407). 웹 로그인과 앱(mobile)이 같이 쓴다.
  이름·휴대폰(마이페이지에서 인증해 둔 번호) → 알림톡 인증번호 → 맞히면 10분짜리 표와 아이디 → 새 비밀번호.
  옛 `/password-reset-requests`(임시 비밀번호 메일)는 이미 깔린 앱을 위해 서버에만 남아 있다.
*/
/** 이름과 휴대폰 번호로 찾는다(2026-10-07). 아이디는 받지 않고, 인증을 맞히면 `loginId` 로 돌아온다. */
export type PasswordResetStartInput = { name: string; phone: string };
export type PasswordResetChallenge = { verificationId: string; maskedPhone: string; expiresInSeconds: number };
export type PasswordResetConfirmInput = { verificationId: string; code: string };
export type PasswordResetCompleteInput = { ticket: string; newPassword: string };

export function usePasswordResetStart() {
  return useMutation({
    mutationFn: (input: PasswordResetStartInput) =>
      apiFetch<PasswordResetChallenge>('/v1/auth/password-reset/start', { method: 'POST', body: input }),
  });
}

export function usePasswordResetConfirm() {
  return useMutation({
    mutationFn: (input: PasswordResetConfirmInput) =>
      apiFetch<{ ticket: string; loginId: string | null }>('/v1/auth/password-reset/confirm', {
        method: 'POST',
        body: input,
      }),
  });
}

export function usePasswordResetComplete() {
  return useMutation({
    mutationFn: (input: PasswordResetCompleteInput) =>
      apiFetch<void>('/v1/auth/password-reset/complete', { method: 'POST', body: input }),
  });
}
